import { Button, Card, Divider, Menu, Popover } from "antd";
import type { ItemType } from "antd/lib/menu/hooks/useItems";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ActionsIcon } from "../../components/ActionsIcon";
import { UpdateDataConsumerButton } from "../../components/integration-platform/actions/UpdateDataConsumerButton";
import { DataConsumerSummary } from "../../components/integration-platform/data-consumer/DataConsumerSummary";
import { IntegrationPanelWrapper } from "../../components/integration-platform/integration/IntegrationPanelWrapper";

export interface DataConsumerInformation {
  consumerName: string;
  contactId: string;
  contactName: string;
  contactEmail: string;
}

export function DataConsumerIntegration() {
  const { t } = useTranslation();
  const { consumerId } = useParams<{ consumerId: string }>();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const isMountedRef = useRef(true);

  const [dataConsumerInformation, setDataConsumerInformation] = useState<
    Partial<DataConsumerInformation>
  >({});

  const [refreshFlags, setRefreshFlags] = useState({
    consumer: false,
  });

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshConsumer = useCallback(() => {
    if (isMountedRef.current) {
      setRefreshFlags((prev) => ({ ...prev, consumer: !prev.consumer }));
    }
  }, []);

  const getDataConsumerInformation = useCallback(
    (info: DataConsumerInformation) => {
      if (isMountedRef.current) {
        setDataConsumerInformation(info);
      }
    },
    []
  );

  const getCompleteDataConsumerInfo = () => ({
    consumerName: dataConsumerInformation.consumerName || "",
    contactId: dataConsumerInformation.contactId || "",
    contactName: dataConsumerInformation.contactName || "",
    contactEmail: dataConsumerInformation.contactEmail || "",
  });

  const actionsMenuItems: ItemType[] = [
    {
      key: "edit",
      label: (
        <UpdateDataConsumerButton
          consumerId={consumerId}
          dataConsumerInformation={getCompleteDataConsumerInfo()}
          refreshConsumer={refreshConsumer}
        />
      ),
      onClick: () => setPopoverOpen(false),
    },
  ];

  const actionsMenu = (
    <Menu
      className="actions-menu"
      style={{ minWidth: 180 }}
      items={actionsMenuItems}
    />
  );

  return (
    <Card
      title={
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
          }}
        >
          <span>{t("integrations:data-consumer-integrations-title")}</span>
          <span></span>
          <Popover
            content={actionsMenu}
            trigger="click"
            open={popoverOpen}
            onOpenChange={(open) => setPopoverOpen(open)}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0 }}
          >
            <Button>
              <ActionsIcon />
            </Button>
          </Popover>
        </div>
      }
      style={{ height: "100%" }}
      bodyStyle={{ height: "92%", display: "flex", flexDirection: "column" }}
    >
      <DataConsumerSummary
        consumerId={consumerId}
        getDataConsumerInformation={getDataConsumerInformation}
        refreshConsumer={refreshFlags.consumer}
      />
      <Divider />
      <IntegrationPanelWrapper
        consumerId={consumerId}
      ></IntegrationPanelWrapper>
    </Card>
  );
}
